<template>
  <View gap="md" direction="column" class="applications-section">
    <TopBarFilters
      :filters="filters"
      :selectedFilter="selectedFilter"
      :counts="counts"
      @selected="selectedFilter = $event"
    />

    <template v-if="selectedFilter === ApplicationBundleFilter.WANTS_TO_APPLY">
      <PlaceholderSection
        v-if="isWantsToApplyLoading || wantsToApply.length === 0"
        :is-loading="isWantsToApplyLoading"
        :icon="'file-question-02'"
        title="No one is waiting to apply"
        description="Renters who ask to apply after their tour, or with a reason to skip it, show up here until you answer them."
      />

      <div v-else class="applications">
        <LeadCard
          v-for="inquiry in wantsToApply"
          :key="inquiry.id"
          :property-inquiry="inquiry"
          show-property
          class="wants-to-apply-card"
          role="button"
          tabindex="0"
          @click="openPanel(inquiry)"
          @keydown.enter.prevent="openPanel(inquiry)"
          @keydown.space.prevent="openPanel(inquiry)"
        />
      </div>

      <WantsToApplyPanel
        v-if="selectedInquiry"
        ref="panelRef"
        :inquiry="selectedInquiry"
        @closed="selectedInquiry = undefined"
      />
    </template>

    <template v-else>
      <PlaceholderSection
        v-if="isLoading || bundles.length === 0"
        :is-loading="isLoading"
        :icon="'file-question-02'"
        :title="getEmptyStateText()"
        :description="getEmptyStateDescription()"
      />

      <div v-else class="applications">
        <ApplicationBundleCard
          v-for="applicationBundle in bundles"
          :key="applicationBundle.id"
          :applicationBundle="applicationBundle"
        />
      </div>
    </template>
  </View>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';

import { ApplicationBundleStatus } from '@tallo/applications';
import { TopBarFilters, View } from '@tallo/design-system';
import { ApplicationBundleCard } from '@tallo/investor/applications';
import { PropertyInquiryDto } from '@tallo/investor/property';

import LeadCard from '../../property/pages/leads/lead-card.vue';
import WantsToApplyPanel from '../../wants-to-apply/wants-to-apply-panel.vue';
import PlaceholderSection from '../placeholder-section.vue';

// In the order an application moves through them; the tabs follow it
enum ApplicationBundleFilter {
  WANTS_TO_APPLY = 'Wants to apply',
  IN_PROGRESS = 'In progress',
  READY_FOR_REVIEW = 'Ready for review',
}

interface Props {
  isLoading: boolean;
  applicationBundles: any[];
  isWantsToApplyLoading: boolean;
  /** Renters waiting for an application, newest ask first */
  wantsToApply: PropertyInquiryDto[];
}

const props = defineProps<Props>();

const filters = Object.values(ApplicationBundleFilter);
const selectedFilter = ref(filters[0]);

const selectedInquiry = ref<PropertyInquiryDto>();
const panelRef = ref<InstanceType<typeof WantsToApplyPanel>>();

async function openPanel(inquiry: PropertyInquiryDto) {
  selectedInquiry.value = inquiry;
  await nextTick();
  panelRef.value?.open();
}

const bundles = computed(() => bundlesUnder(selectedFilter.value));

// The two that wait on the owner
const counts = computed(
  () =>
    new Map([
      [ApplicationBundleFilter.WANTS_TO_APPLY, props.wantsToApply.length],
      [ApplicationBundleFilter.READY_FOR_REVIEW, bundlesUnder(ApplicationBundleFilter.READY_FOR_REVIEW).length],
    ]),
);

function bundlesUnder(filter: ApplicationBundleFilter) {
  return props.applicationBundles
    .filter((bundle) => {
      switch (filter) {
        case ApplicationBundleFilter.READY_FOR_REVIEW:
          return bundle.status === ApplicationBundleStatus.SUBMITTED;

        // An open info request is back in the renter's hands, so it waits here rather than under review.
        case ApplicationBundleFilter.IN_PROGRESS:
          return [
            ApplicationBundleStatus.SENT,
            ApplicationBundleStatus.IN_PROGRESS,
            ApplicationBundleStatus.UPDATE_REQUESTED,
          ].includes(bundle.status);

        default:
          return false;
      }
    })
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
}

function getEmptyStateText(): string {
  switch (selectedFilter.value) {
    case ApplicationBundleFilter.READY_FOR_REVIEW:
      return 'No applications ready for review';
    case ApplicationBundleFilter.IN_PROGRESS:
      return 'No applications in progress';
    default:
      return 'No applications available';
  }
}

function getEmptyStateDescription(): string {
  switch (selectedFilter.value) {
    case ApplicationBundleFilter.READY_FOR_REVIEW:
      return 'Applications that are ready for your review will appear here once renters submit them.';
    case ApplicationBundleFilter.IN_PROGRESS:
      return 'Applications that are currently being processed will appear here.';
    default:
      return 'Applications will appear here once renters start applying to your properties.';
  }
}
</script>

<style scoped lang="scss">
.applications-section {
  height: 100%;
}

.applications {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(20rem, 1fr));
  gap: var(--t-spacing-md);
}

.wants-to-apply-card {
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid var(--t-color-neutral-950);
    outline-offset: 2px;
  }
}
</style>
